import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  Badge,
  HStack,
  Icon,
} from "@chakra-ui/react";
import axios from "axios";
import * as mod from "../../url";
import Sidebar from "../Sidebar";
import Navbar from "../Navbar/Navbar";
import { SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import * as XLSX from "xlsx";
import { Link, useNavigate } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
const AllCandidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const toast = useToast();

  const storedData = JSON.parse(sessionStorage.getItem("superAdminLawvs"));
  const token = storedData?.data?.token;
  const config = { headers: { Authorization: `${token}` } };

  const SIDEBAR_WIDTH = "250px";
  const Candidate_PER_PAGE = 25;

  const totalCandidatePages = Math.ceil(
    filteredCandidates.length / Candidate_PER_PAGE,
  );

  const startIndex = (currentPage - 1) * Candidate_PER_PAGE;
  const endIndex = startIndex + Candidate_PER_PAGE;

  const paginatedCandidate = filteredCandidates.slice(startIndex, endIndex);
  // 🔹 Fetch all candidates
  const fetchCandidates = async () => {
    try {
      const { data } = await axios.get(
        `${mod.api_url}/api/v1/election/candidate/get-candidate`,
        config,
      );
      setCandidates(data || []);
    } catch (error) {
      toast({
        title: "Error fetching candidates",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  // 🔹 Filter (search + date)
  useEffect(() => {
    let filtered = [...candidates];

    if (search.trim()) {
      filtered = filtered.filter(
        (c) =>
          c.name?.toLowerCase().includes(search.toLowerCase()) ||
          c.email?.toLowerCase().includes(search.toLowerCase()) ||
          c.mobile?.includes(search),
      );
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filtered = filtered.filter((c) => {
        const created = new Date(c.createdAt);
        return created >= start && created <= end;
      });
    }

    setFilteredCandidates(filtered);
    setCurrentPage(1);
  }, [search, startDate, endDate, candidates]);

  // 🔹 Toggle verification
  const handleVerifyToggle = async (id, isVerified) => {
    try {
      await axios.put(
        `${mod.api_url}/api/v1/election/candidate/verify/${id}`,
        { isVerified: !isVerified },
        config,
      );

      setCandidates((prev) =>
        prev.map((c) => (c._id === id ? { ...c, isVerified: !isVerified } : c)),
      );

      toast({
        title: "Candidate status updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to update status",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 🔹 Export Excel
  const exportToExcel = () => {
    if (filteredCandidates.length === 0) {
      toast({
        title: "No data to export",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(filteredCandidates);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
    XLSX.writeFile(workbook, "Candidates.xlsx");
  };

  return (
    <>
      <Sidebar />
      <Navbar />

      <Box
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
        mt="70px"
        p={5}
        bg="gray.50"
        minH="100vh"
      >
        <Container maxW="7xl">
          <Heading
            color="white"
            textAlign="center"
            fontSize="2xl"
            mb={6}
            bg="teal.600"
            p={4}
            borderRadius="md"
          >
            Election Candidates
          </Heading>

          {/* Filters */}
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            gap={4}
            bg="white"
            p={4}
            borderRadius="lg"
            boxShadow="sm"
            mb={6}
          >
            <InputGroup w={{ base: "100%", md: "30%" }}>
              <InputLeftElement>
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by name, email or mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>

            <Flex gap={3}>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Text>to</Text>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Flex>

            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="teal"
              onClick={exportToExcel}
            >
              Export Excel
            </Button>
          </Flex>

          {/* Table */}
          <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
            <Text fontSize="sm" p={3} textAlign="right">
              Total Records: {filteredCandidates.length}
            </Text>

            <Table variant="striped">
              <Thead bg="teal.600">
                <Tr>
                  <Th color="white">#</Th>
                  <Th color="white">Name</Th>
                  <Th color="white">photo</Th>
                  <Th color="white">Email</Th>
                  <Th color="white">Mobile</Th>
                  <Th color="white">Status</Th>
                  <Th color="white">Chat</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedCandidate.map((c, index) => (
                  <Tr key={c._id}>
                    <Td>{index + 1}</Td>
                    <Td
                      fontWeight="medium"
                      cursor="pointer"
                      color="blue.600"
                      _hover={{ textDecoration: "underline" }}
                      onClick={() => navigate(`/admin/candidate/${c._id}`)}
                    >
                      {c.name.toUpperCase()}
                    </Td>
                    <Td style={{ maxHeight: "200px", maxWidth: "100px" }}>
                      <img
                        src={`${mod.api_url}${c?.photo}`}
                        borderRadius="md"
                        boxShadow="md"
                      />
                    </Td>
                    <Td>{c.email}</Td>
                    <Td>{c.mobile}</Td>
                    {/* <Td>{c.category?.name || "N/A"}</Td> */}
                    {/* <Td>
                      <Badge colorScheme={c.isVerified ? "green" : "orange"}>
                        {c.isVerified ? "Verified" : "Pending"}
                      </Badge>
                    </Td> */}
                    <Td>
                      <Button
                        size="sm"
                        colorScheme={c.isVerified ? "green" : "red"}
                        onClick={() => handleVerifyToggle(c._id, c.isVerified)}
                      >
                        {c.isVerified ? "Verified" : "UnVerified"}
                      </Button>
                    </Td>

                    <Td>
                      <HStack spacing={2}>
                        {c.mobile && (
                          <Icon
                            as={FaWhatsapp}
                            boxSize={7}
                            color="pink.600"
                            cursor="pointer"
                            onClick={() =>
                              window.open(
                                `https://wa.me/91${c.mobile}`,
                                "_blank",
                              )
                            }
                            _hover={{ color: "green.600" }}
                          />
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/*  GLOBAL CATEGORY PAGINATION */}
          <HStack justify="center" spacing={4} mt={10}>
            <Button
              isDisabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </Button>

            <Text>
              Page <b>{currentPage}</b> of <b>{totalCandidatePages}</b>
            </Text>

            <Button
              isDisabled={currentPage === totalCandidatePages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </Button>
          </HStack>
        </Container>
      </Box>
    </>
  );
};

export default AllCandidates;
